
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, X, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export default function BulkImportEAStaff({ onClose, onSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState("");
  const [importData, setImportData] = useState("");

  const processImportData = async () => {
    if (!importData.trim()) {
      setError("Please paste EA staff data");
      return;
    }

    setProcessing(true);
    setError(null);
    setProgress("Parsing data...");

    try {
      const lines = importData.trim().split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error("No data found");
      }

      // Parse header line
      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());
      
      // Map column indices
      const colMap = {
        fullName: headers.findIndex(h => h.includes('full name') || h.includes('name')),
        email: headers.findIndex(h => h.includes('email')),
      };

      // Check required columns
      if (colMap.email === -1) {
        throw new Error("Missing required column: Email");
      }

      setProgress("Loading existing users...");
      const existingUsers = await base44.entities.User.list();
      
      let updatedCount = 0;
      let notFoundCount = 0;
      const notFoundEmails = [];

      // Process data lines (skip header)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        setProgress(`Processing ${i} of ${lines.length - 1}...`);

        const email = cols[colMap.email];
        const fullName = colMap.fullName !== -1 ? cols[colMap.fullName] : '';

        // Skip if missing email
        if (!email) {
          continue;
        }

        // Check for existing user by email
        const existingUser = existingUsers.find(
          u => u.email?.toLowerCase() === email.toLowerCase()
        );

        if (existingUser) {
          // Update existing user to EA staff
          await base44.entities.User.update(existingUser.id, {
            user_type: 'ea_staff',
            full_name: fullName || existingUser.full_name
          });
          updatedCount++;
        } else {
          notFoundCount++;
          notFoundEmails.push(email);
        }
      }

      let successMsg = `✅ Success! Updated ${updatedCount} users to EA staff.`;
      if (notFoundCount > 0) {
        successMsg += ` ${notFoundCount} users not found in system (need to be invited first): ${notFoundEmails.slice(0, 3).join(', ')}${notFoundCount > 3 ? '...' : ''}`;
      }
      
      setSuccess(successMsg);
      
      setTimeout(() => {
        onSuccess();
      }, 3000);

    } catch (err) {
      console.error("Processing error:", err);
      setError(err.message || "Failed to process data. Make sure you copied the complete data with all columns.");
    } finally {
      setProcessing(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Update EA Staff
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={processing}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 text-green-900 border-green-200">
              <CheckCircle className="w-4 h-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {progress && processing && (
            <Alert className="bg-blue-50 text-blue-900 border-blue-200">
              <Loader2 className="w-4 h-4 animate-spin" />
              <AlertDescription>{progress}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <Alert className="bg-orange-50 border-orange-200">
              <AlertCircle className="w-4 h-4 text-orange-600" />
              <AlertDescription className="text-orange-900">
                <strong>⚠️ Important:</strong> Users must be invited to Base44 first through Dashboard → Users → Invite. This tool only updates existing users to have EA staff permissions.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Alert className="mb-4 bg-blue-50 border-blue-200">
                <AlertDescription className="text-sm space-y-2">
                  <div>
                    <strong>📋 Instructions:</strong>
                  </div>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Open your EA staff list in Excel/CSV</li>
                    <li><strong>Required column:</strong> Email</li>
                    <li><strong>Optional column:</strong> Full Name</li>
                    <li>Select ALL columns from header row down</li>
                    <li>Copy (Ctrl+C or Cmd+C)</li>
                    <li>Paste below (Ctrl+V or Cmd+V)</li>
                    <li>Click &quot;Update EA Staff&quot;</li>
                  </ol>
                  <div className="text-xs text-blue-700 mt-2">
                    <p>ℹ️ Only users already in the system will be updated to EA staff permissions.</p>
                  </div>
                </AlertDescription>
              </Alert>
              <Textarea
                value={importData}
                onChange={(e) => {
                  setImportData(e.target.value);
                  setError(null);
                }}
                placeholder="Paste EA staff data here (must include Email column)..."
                className="h-80 font-mono text-xs"
                disabled={processing}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={processImportData}
              disabled={!importData.trim() || processing}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Update EA Staff
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
